import React from 'react';
import { Award, Users, Target, Heart } from 'lucide-react';
import { Card } from '../components/ui/card';
import { teamMembers } from '../mockData';
import { useSiteSettings } from '../context/SiteSettingsContext';

const About = () => {
  const { settings, loading } = useSiteSettings();

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-[#D4AF37]">{settings.company_name}</span>
            </h1>
            <p className="text-2xl text-[#D4AF37] mb-4 font-semibold italic">
              "{settings.slogan}"
            </p>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 leading-relaxed">
              {settings.about_text}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_homecraft-17/artifacts/0c1g8duh_20251111_191316.jpg" 
                alt="BuildBroz Kitchen Remodeling" 
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Our Story</h2>
              <div className="w-16 h-1 bg-[#D4AF37] mb-6"></div>
              <div className="space-y-4 text-gray-300">
                <p>
                  BuildBroz was founded in 2025 with a mission to bring professional, high-quality remodeling services to Cleveland and the surrounding communities. With 15 years of hands-on experience in residential remodeling, our owner understands what it takes to transform a house into the home you've always dreamed of.
                </p>
                <p>
                  Our extensive background ensures that every project, from kitchen renovations to complete home transformations, is handled with expertise and care. We believe that every home deserves thoughtful craftsmanship and personalized attention to detail.
                </p>
                <p>
                  Based in Cleveland, Ohio, we're proud to serve our local community. Whether it's updating a single room or remodeling your entire home, we bring the same dedication and quality to every project, treating each home as if it were our own.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: Award, 
                title: 'Excellence', 
                desc: 'We pursue perfection in every detail, never settling for good enough' 
              },
              { 
                icon: Users, 
                title: 'Collaboration', 
                desc: 'Working closely with clients to bring their vision to life' 
              },
              { 
                icon: Target, 
                title: 'Integrity', 
                desc: 'Honest communication and transparent processes every step of the way' 
              },
              { 
                icon: Heart, 
                title: 'Passion', 
                desc: 'Genuine love for the craft of building beautiful, lasting homes' 
              }
            ].map((value, idx) => (
              <Card key={idx} className="bg-[#2A2A2A] border-gray-700 p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <value.icon className="mx-auto mb-4 text-[#D4AF37]" size={48} />
                <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400">{value.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Work</h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Examples of our quality remodeling projects
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="bg-[#1A1A1A] border-gray-800 overflow-hidden group">
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent"></div>
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-white mb-2">{member.name}</h3>
                  <p className="text-[#D4AF37]">{member.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '15+', label: 'Years Experience' },
              { number: '100+', label: 'Homes Remodeled' },
              { number: '100%', label: 'Satisfaction Rate' },
              { number: 'Local', label: 'Cleveland Based' }
            ].map((stat, idx) => (
              <div key={idx}>
                <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-2">{stat.number}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;